import { RootLayout } from "@/components/layout/root-layout";
import { createRootRoute, createRoute, createRouter } from "@tanstack/react-router";
import { lazy } from "react";

// Eager-load dashboard (most visited), lazy-load the rest
import { DashboardPage } from "@/routes/dashboard";

const ReviewPage = lazy(() => import("@/routes/review").then((m) => ({ default: m.ReviewPage })));
const IngestPage = lazy(() => import("@/routes/ingest").then((m) => ({ default: m.IngestPage })));
const SearchPage = lazy(() => import("@/routes/search").then((m) => ({ default: m.SearchPage })));
const AskPage = lazy(() => import("@/routes/ask").then((m) => ({ default: m.AskPage })));
const GraphPage = lazy(() => import("@/routes/graph").then((m) => ({ default: m.GraphPage })));
const VectorKbPage = lazy(() =>
	import("@/routes/vector-kb").then((m) => ({ default: m.VectorKbPage })),
);
const VideoQueuePage = lazy(() =>
	import("@/routes/video-queue").then((m) => ({ default: m.VideoQueuePage })),
);
const BookQueuePage = lazy(() =>
	import("@/routes/book-queue").then((m) => ({ default: m.BookQueuePage })),
);
const KnowledgeAtlasPage = lazy(() =>
	import("@/routes/knowledge-atlas").then((m) => ({
		default: m.KnowledgeAtlasPage,
	})),
);
const GapQueuePage = lazy(() =>
	import("@/routes/gap-queue").then((m) => ({
		default: m.GapQueuePage,
	})),
);
const SynthesisPage = lazy(() =>
	import("@/routes/synthesis").then((m) => ({
		default: m.SynthesisPage,
	})),
);
const CreatorPage = lazy(() =>
	import("@/routes/creator").then((m) => ({
		default: m.CreatorPage,
	})),
);
const DocumentsPage = lazy(() =>
	import("@/routes/documents/index").then((m) => ({
		default: m.DocumentsPage,
	})),
);
const DocumentDetailPage = lazy(() =>
	import("@/routes/documents/detail").then((m) => ({
		default: m.DocumentDetailPage,
	})),
);
const StatsPage = lazy(() => import("@/routes/stats").then((m) => ({ default: m.StatsPage })));
const SettingsPage = lazy(() =>
	import("@/routes/settings").then((m) => ({ default: m.SettingsPage })),
);
const AiModelsPage = lazy(() =>
	import("@/routes/ai-models").then((m) => ({ default: m.AiModelsPage })),
);

const rootRoute = createRootRoute({
	component: RootLayout,
});

const dashboardRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/",
	component: DashboardPage,
});

const reviewRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/review",
	component: ReviewPage,
});

const ingestRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/ingest",
	component: IngestPage,
});

const searchRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/search",
	component: SearchPage,
});

const askRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/ask",
	component: AskPage,
});

const graphRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/graph",
	component: GraphPage,
});

const vectorKbRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/vector-kb",
	component: VectorKbPage,
});

const videoQueueRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/video-queue",
	component: VideoQueuePage,
});

const bookQueueRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/book-queue",
	component: BookQueuePage,
});

const knowledgeAtlasRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/atlas",
	component: KnowledgeAtlasPage,
});

const gapQueueRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/gap-queue",
	component: GapQueuePage,
});

const synthesisRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/synthesis",
	component: SynthesisPage,
});

const creatorRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/creator",
	component: CreatorPage,
});

const documentsRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/documents",
	component: DocumentsPage,
});

const documentDetailRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/documents/$id",
	component: DocumentDetailPage,
});

const statsRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/stats",
	component: StatsPage,
});

const settingsRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/settings",
	component: SettingsPage,
});

const aiModelsRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/ai-models",
	component: AiModelsPage,
});

const routeTree = rootRoute.addChildren([
	dashboardRoute,
	reviewRoute,
	ingestRoute,
	searchRoute,
	askRoute,
	graphRoute,
	vectorKbRoute,
	videoQueueRoute,
	bookQueueRoute,
	knowledgeAtlasRoute,
	gapQueueRoute,
	synthesisRoute,
	creatorRoute,
	documentsRoute,
	documentDetailRoute,
	statsRoute,
	settingsRoute,
	aiModelsRoute,
]);

export const router = createRouter({
	routeTree,
	defaultPreload: "intent",
});

declare module "@tanstack/react-router" {
	interface Register {
		router: typeof router;
	}
}
